import React, { Component } from "react";
import DateTimePicker from "react-datetime-picker";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import styled from "styled-components";
import Loader from "./common/loader";
import { saveJournal, getTagsList } from "../services/journalService";
import { saveDraft, loadDraft, clearDraft } from "./utils/draftStorage";
import extractTags from "./utils/extractTags";
import TagPicker from "./tagPicker";

const Togglers = styled.div`
  display: flex;
  gap: 1rem;
  align-self: flex-end;
  margin-bottom: 0.5rem;
`;

const TogglerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  padding: 0;
  font-size: 0.8rem;
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.paginationBtn : theme.text)};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
`;

const TogglerTrack = styled.span`
  position: relative;
  display: inline-block;
  width: 1.8rem;
  height: 1rem;
  border-radius: 1rem;
  background: ${({ theme, $active }) => ($active ? theme.paginationBtn : theme.body)};
  border: ${({ theme }) => theme.cardBorder};
  transition: background 0.15s ease;
`;

const TogglerKnob = styled.span`
  position: absolute;
  top: 1px;
  left: ${({ $active }) => ($active ? "calc(100% - 0.9rem - 1px)" : "1px")};
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.card};
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.4);
  transition: left 0.15s ease;
`;

const TextArea = styled.textarea`
  background-color: ${({ theme }) => theme.card} !important;
  color: ${({ theme }) => theme.text} !important;
  font-family: "JetBrains Mono", monospace;
  letter-spacing: 0;
  font-size: 1rem;
  line-height: 1.7;
  width: 100%;
  resize: none;
`;

class MyForm extends Component {
  state = {
    journal: {
      comment: "",
      date: new Date(),
      starred: false,
      locked: false,
    },
    rows: 10,
    loading: false,
    arabic: false,
    // Length of comment at the moment Arabic mode was last switched on - the
    // transliteration lookback (below) never reaches back past this point,
    // so text typed before toggling Arabic on is never touched.
    arabicBoundary: 0,
    hidden: false,
    selectedTags: [],
    tagOptions: [],
  };

  textareaRef = React.createRef();

  getDraftId = () => (this.props.journal && this.props.journal._id) || "new";

  saveDraftDebounced = debounce(journal => {
    saveDraft(this.getDraftId(), journal);
  }, 500);

  flushDraft = () => {
    this.saveDraftDebounced.flush();
  };

  componentDidMount() {
    const draft = loadDraft(this.getDraftId());
    let journal = this.state.journal;

    if (draft) {
      journal = draft;
      this.setState({ journal });
      toast.info("Restored your unsaved draft.");
    } else if (this.props.journal) {
      journal = this.props.journal;
      this.setState({ journal });
    }

    // Only tags already saved to the backend are treated as a sticky
    // baseline; tags currently just typed in the text are handled live via
    // derivedTags in render, so editing/correcting a #hashtag before saving
    // never leaves a stale tag behind.
    this.setState({ selectedTags: journal.tags || [] });

    getTagsList().then(({ data }) => {
      this.setState({ tagOptions: data.map(t => t.tag) });
    });

    document.addEventListener('keydown',this.keydownHandler);
    window.addEventListener('beforeunload', this.flushDraft);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown',this.keydownHandler);
    window.removeEventListener('beforeunload', this.flushDraft);
    this.flushDraft();
  }

  keydownHandler = e => {
    if(e.keyCode===13 && e.ctrlKey) this.handleSave();
  }

  handleChange = e => {
    const journal = { ...this.state.journal };
    journal["comment"] = e.currentTarget.value;
    this.setState({ journal });
    this.saveDraftDebounced(journal);
  };

  handleTagsChange = selectedTags => {
    this.setState({ selectedTags });
  };

  wordCount = str =>
    str
      .replace(/\n/g, " ")
      .split(" ")
      .filter(n => n !== "").length;

  handleDateChange = value => {
    const journal = { ...this.state.journal };
    journal["date"] = value;
    this.setState({ journal });
    this.saveDraftDebounced(journal);
  };

  handleSave = async () => {
    const { journal, selectedTags } = this.state;
    const { date } = journal;

    if (!date) return toast.error("Invalide date.");

    const finalTags = [
      ...new Set([...selectedTags, ...extractTags(journal.comment)]),
    ];

    if (journal._id) console.log("goooothem, you modified");
    else console.log("ooh it's new");

    if (journal._id) {
      this.saveDraftDebounced.cancel();
      clearDraft(this.getDraftId());
      this.props.onToggleEdit();
      this.props.onSave({ ...this.state.journal, tags: finalTags });
      return await saveJournal({
        _id: journal._id,
        ...journal,
        tags: finalTags,
      });
    }

    this.setState({ loading: true });
    const { data: newJournal } = await saveJournal({
      ...this.state.journal,
      tags: finalTags,
    });
    this.saveDraftDebounced.cancel();
    clearDraft(this.getDraftId());
    this.setState({ loading: false });
    this.props.history.push(`/journals/${newJournal._id}`);
  };

  handleRowsChange = () => {
    const rows = this.state.rows === 10 ? 30 : 10;
    this.setState({ rows });
  };

  // Longest raw keystroke sequence any combo pattern below looks back across
  // (e.g. "==a", or an apostrophe modifying an already-transliterated letter
  // plus a preceding combined letter like "وءء" -> "ؤ").
  ARABIC_LOOKBACK = 3;

  transliterateArabic = text =>
    text
      .replace(/==a/g, "ً")
      .replace(/==i/g, "ٍ")
      .replace(/==u/g, "ٌ")
      .replace(/=a/g, "َ")
      .replace(/=i/g, "ِ")
      .replace(/=u/g, "ُ")
      .replace(/=w/g, "ّ")
      .replace(/=o/g, "ْ")
      .replace(/’/g, "'")
      .replace(/[aâàā]/g, "ا")
      .replace(/اا/g, "آ")
      .replace(/b/g, "ب")
      .replace(/ب'/g, "پ")
      .replace(/p/g, "پ")
      .replace(/پ'/g, "ب")
      .replace(/t/g, "ت")
      .replace(/ت'/g, "ث")
      .replace(/ث'/g, "ت")
      .replace(/ṯ/g, "ث")
      .replace(/[jǧ]/g, "ج")
      .replace(/ج'/g, "چ")
      .replace(/c/g, "چ")
      .replace(/چ'/g, "ح")
      .replace(/[HḥḤ]/g, "ح")
      .replace(/ح'/g, "خ")
      .replace(/[xẖK]/g, "خ")
      .replace(/خ'/g, "ج")
      .replace(/d/g, "د")
      .replace(/د'/g, "ذ")
      .replace(/ذ'/g, "د")
      .replace(/ḏ/g, "ذ")
      .replace(/r/g, "ر")
      .replace(/ر'/g, "ز")
      .replace(/ز'/g, "ر")
      .replace(/z/g, "ز")
      .replace(/s/g, "س")
      .replace(/س'/g, "ش")
      .replace(/ش'/g, "س")
      .replace(/š/g, "ش")
      .replace(/[Sṣ]/g, "ص")
      .replace(/ص'/g, "ض")
      .replace(/ض'/g, "ص")
      .replace(/[Dḍ]/g, "ض")
      .replace(/[Tṭ]/g, "ط")
      .replace(/ط'/g, "ظ")
      .replace(/ظ'/g, "ط")
      .replace(/[Zẓ]/g, "ظ")
      .replace(/[gʿ]/g, "ع")
      .replace(/ع'/g, "غ")
      .replace(/غ'/g, "ع")
      .replace(/ġ/g, "غ")
      .replace(/f/g, "ف")
      .replace(/ف'/g, "ڤ")
      .replace(/ڤ'/g, "ف")
      .replace(/v/g, "ڢ")
      .replace(/q/g, "ق")
      .replace(/ق'/g, "ڨ")
      .replace(/ڨ'/g, "ق")
      .replace(/k/g, "ك")
      .replace(/ك'/g, "ڭ")
      .replace(/ڭ'/g, "ك")
      .replace(/l/g, "ل")
      .replace(/m/g, "م")
      .replace(/n/g, "ن")
      .replace(/h/g, "ه")
      .replace(/ه'/g, "ة")
      .replace(/ة'/g, "ه")
      .replace(/[wouôûōū]/g, "و")
      .replace(/[yieîī]/g, "ي")
      .replace(/[YIE]/g, "ى")
      .replace(/ي'/g, "ى")
      .replace(/ى'/g, "ي")
      .replace(/-/g, "ء")
      .replace(/ʾ/g, "ء")
      .replace(/وءء/g, "ؤ")
      .replace(/يءء/g, "ئ")
      .replace(/اءء/g, "إ")
      .replace(/I/g, "إ")
      .replace(/A/g, "إ")
      .replace(/ءا/g, "أ")
      .replace(/_/g, "ـ")
      .replace(/\?/g, "؟")
      .replace(/;/g, "؛")
      .replace(/,/g, "،");

  arabicHandler = e => {
    const journal = { ...this.state.journal };
    const oldText = journal.comment || "";
    const newText = e.currentTarget.value;

    // Find the changed region only, so unrelated text elsewhere isn't
    // re-run through the transliteration chain.
    let start = 0;
    while (
      start < oldText.length &&
      start < newText.length &&
      oldText[start] === newText[start]
    )
      start++;

    let oldEnd = oldText.length;
    let newEnd = newText.length;
    while (
      oldEnd > start &&
      newEnd > start &&
      oldText[oldEnd - 1] === newText[newEnd - 1]
    ) {
      oldEnd--;
      newEnd--;
    }

    // arabicBoundary marks where pre-toggle text ends. Keep it in sync with
    // this edit: typing at/after it leaves it in place; editing purely
    // before it shifts it by however much that region grew/shrank; an edit
    // straddling it (e.g. select-all-delete) collapses it to the edit's
    // start, so clearing everything and starting fresh drops the boundary
    // to 0 instead of leaving it pointing past the now-empty text.
    let arabicBoundary = this.state.arabicBoundary;
    if (start >= arabicBoundary) {
      // unchanged
    } else if (oldEnd <= arabicBoundary) {
      arabicBoundary += newEnd - oldEnd;
    } else {
      arabicBoundary = start;
    }

    // Widen the window backwards by a few already-committed characters so
    // multi-keystroke combos (e.g. "==a", "b'") that span the boundary
    // between old and new text still resolve correctly - but never past
    // arabicBoundary, so text typed before Arabic mode was switched on is
    // never re-transliterated.
    const windowStart = Math.max(arabicBoundary, start - this.ARABIC_LOOKBACK);
    const rawWindow = newText.slice(windowStart, newEnd);
    const transliteratedWindow = this.transliterateArabic(rawWindow);

    journal["comment"] =
      newText.slice(0, windowStart) +
      transliteratedWindow +
      newText.slice(newEnd);

    // Setting a controlled textarea's value programmatically (which React
    // does here, since the transliterated text differs from what the
    // browser just typed) resets the caret to the end of the field. Put it
    // back where editing actually happened, so typing mid-text doesn't get
    // kicked to the end after every keystroke.
    const cursorPos = windowStart + transliteratedWindow.length;

    this.setState({ journal, arabicBoundary }, () => {
      const el = this.textareaRef.current;
      if (el) el.setSelectionRange(cursorPos, cursorPos);
    });
    this.saveDraftDebounced(journal);
  };

  toggleArabic = () => {
    this.setState(({ arabic, journal }) => ({
      arabic: !arabic,
      // Switching on: nothing before this point should ever be touched by
      // the transliteration lookback. Switching off: irrelevant until it's
      // switched on again, at which point this gets set fresh.
      arabicBoundary: !arabic ? (journal.comment || "").length : 0,
    }));
  };

  handleCancel = () => {
    this.saveDraftDebounced.cancel();
    clearDraft(this.getDraftId());
    this.props.onToggleEdit();
  };

  render() {
    const {
      journal,
      rows,
      loading,
      arabic,
      hidden,
      selectedTags,
      tagOptions,
    } = this.state;
    const rowChangerStyle = {
      cursor: "pointer",
      borderBottom: "solid 2px #007bff",
      padding: "5px",
      marginBottom: "10px",
    };

    return (
      <div className="container">
        <div className="form-group d-flex flex-column align-items-center m-2">
          {loading && <Loader fontSize="2rem" />}
          <span style={{ fontSize: "0.8rem", alignSelf: "flex-end" }}>
            {this.wordCount(journal.comment)}
          </span>
          <TextArea
            ref={this.textareaRef}
            style={{ opacity: hidden ? "0.1" : "1" }}
            dir="auto"
            placeholder="How was your day? ..."
            disabled={loading === true}
            autoFocus
            value={journal["comment"]}
            onChange={arabic ? this.arabicHandler : this.handleChange}
            className="form-control"
            id="form"
            rows={rows}
          ></TextArea>
          <TagPicker
            selectedTags={selectedTags}
            derivedTags={extractTags(journal.comment || "")}
            options={tagOptions}
            onChange={this.handleTagsChange}
          />
          <Togglers>
            <TogglerButton
              type="button"
              $active={hidden}
              onClick={() => this.setState({ hidden: !hidden })}
              aria-pressed={hidden}
            >
              <TogglerTrack $active={hidden}>
                <TogglerKnob $active={hidden} />
              </TogglerTrack>
              hidden
            </TogglerButton>
            <TogglerButton
              type="button"
              $active={arabic}
              onClick={this.toggleArabic}
              aria-pressed={arabic}
            >
              <TogglerTrack $active={arabic}>
                <TogglerKnob $active={arabic} />
              </TogglerTrack>
              arabic
            </TogglerButton>
          </Togglers>
          <div onClick={this.handleRowsChange} style={rowChangerStyle}>
            <i
              className={`fa fa-arrow-${rows === 10 ? "down" : "up"} p-1`}
              aria-hidden="true"
            ></i>
          </div>
          <div className="w-100 mb-2">
            <DateTimePicker
              maxDate={new Date("3000")}
              disabled={loading === true}
              onChange={this.handleDateChange}
              value={new Date(journal["date"])}
              clearIcon={null}
            />
            <button
              type="button"
              onClick={() => this.handleDateChange(new Date())}
              className="btn btn-outline-secondary btn-sm ml-2"
            >
              <i className="fa fa-clock-o mr-1" aria-hidden="true"></i>
              Now
            </button>
          </div>
          <div className="w-100">
            <button
              className="btn btn-primary m-2"
              onClick={this.handleSave}
              disabled={journal.comment === "" || loading}
            >
              Save
            </button>
            <button
              className="btn btn-danger m-2"
              onClick={this.handleCancel}
              hidden={!journal._id}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }
}

MyForm.propTypes = {
  journal: PropTypes.object,
  onToggleEdit: PropTypes.func,
  onSave: PropTypes.func,
  theme: PropTypes.string,
};

export default MyForm;
